import React, { useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Input, Modal, Tag, message } from "antd";
import { DeleteOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import useAssistantManagement from "../../../assistantDashboard/hooks/useAssistantManagement";
import { ASSISTANT_ROUTES } from "../../../routes/patientRoutes/routePaths";
import "./AssistantsDirectory.css";

const STATUS_COLORS = {
  available: "green",
  busy: "orange",
  offline: "default",
};

const EMPTY_FORM = {
  fullName: "",
  email: "",
  phone: "",
};

const DEFAULT_PHONE_COUNTRY_CODE =
  import.meta.env.VITE_DEFAULT_PHONE_COUNTRY_CODE || "+216";

const normalizePhone = (value) =>
  String(value || "")
    .trim()
    .replace(/[\s()-]/g, "")
    .replace(/^00/, "+")
    .replace(/^(\d{8})$/, `${DEFAULT_PHONE_COUNTRY_CODE}$1`);

const isValidPhone = (value) => /^\+[1-9]\d{7,14}$/.test(String(value || ""));

const normalizeId = (value) => {
  const id = String(value ?? "").trim();
  return id && !["null", "undefined"].includes(id.toLowerCase()) ? id : "";
};

const pickActivationLink = (...sources) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    const link =
      source.inviteLink ||
      source.activationLink ||
      source.data?.inviteLink ||
      source.data?.activationLink ||
      source.assistant?.inviteLink ||
      source.assistant?.activationLink;

    if (link && String(link).trim()) return String(link).trim();

    const token =
      source.token ||
      source.inviteToken ||
      source.activationToken ||
      source.data?.token ||
      source.data?.inviteToken ||
      source.data?.activationToken ||
      source.assistant?.token ||
      source.assistant?.inviteToken ||
      source.assistant?.activationToken;

    if (token && String(token).trim() && typeof window !== "undefined") {
      const origin = window.location.origin;
      return `${origin}${ASSISTANT_ROUTES.INVITATION_ACTIVATION}?token=${encodeURIComponent(
        String(token).trim()
      )}`;
    }
  }

  return null;
};

export default function AssistantsDirectory() {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.doctor?.user || {});
  const doctorId = normalizeId(
    currentUser?.doctorId ||
      currentUser?.user?.doctorId ||
      currentUser?.id ||
      currentUser?.userId ||
      currentUser?._id ||
      currentUser?.raw?.doctorId ||
      currentUser?.raw?.id ||
      currentUser?.raw?._id
  );
  const {
    assistants,
    listLoading,
    listError,
    loadAssistants,
    loadAssistantById,
    createAssistantEntry,
    removeAssistant,
  } = useAssistantManagement();
  const [query, setQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isInviting, setIsInviting] = useState(false);
  const [deletingAssistantId, setDeletingAssistantId] = useState("");
  const [deleteTargetId, setDeleteTargetId] = useState("");

  useEffect(() => {
    if (doctorId) loadAssistants({ doctorId });
  }, [doctorId, loadAssistants]);

  const filteredAssistants = useMemo(() => {
    const q = query.trim().toLowerCase();
    const source = (Array.isArray(assistants) ? assistants : []).filter((assistant) => {
      const assistantDoctorId = normalizeId(
        assistant?.doctorId || assistant?.doctor?.id || assistant?.doctor?._id
      );
      return !doctorId || assistantDoctorId === doctorId;
    });
    if (!q) return source;

    return source.filter((assistant) => {
      const haystack = [
        assistant.fullName,
        assistant.email,
        assistant.phone,
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [assistants, doctorId, query]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddAssistant = async () => {
    const fullName = formData.fullName.trim();
    const email = formData.email.trim();
    const phone = normalizePhone(formData.phone);
    if (!fullName || !email || !phone || isInviting) return;

    const payload = {
      fullName,
      email,
      phone,
    };

    if (!isValidPhone(phone)) {
      message.error("Phone must be in format +XXXXXXXX (8 to 15 digits).");
      return;
    }

    setIsInviting(true);

    try {
      const created = await createAssistantEntry(payload).unwrap();
      setFormData(EMPTY_FORM);
      setIsModalOpen(false);

      message.success(t("assistantPages.directory.inviteEmailSent"));

      const createdId = created?.id || created?.assistantId;
      let detailsPayload = null;

      if (createdId) {
        try {
          detailsPayload = await loadAssistantById(createdId).unwrap();
        } catch (_) {
          // If detail fails, continue with available payloads.
        }
      }

      const activationLink = pickActivationLink(detailsPayload, created);
      if (activationLink) {
        Modal.info({
          title: t("assistantPages.directory.inviteLinkTitle", "Invitation link"),
          content: (
            <a href={activationLink} target="_blank" rel="noreferrer">
              {t("assistantPages.directory.openActivationLink", "Open activation link")}
            </a>
          ),
          okText: t("common.ok", "OK"),
        });

        // Open in a separate tab to keep the current doctor session untouched.
        window.open(activationLink, "_blank", "noopener,noreferrer");
      }

      loadAssistants({ doctorId });
    } catch (error) {
      const errorMessage =
        error?.message || error?.data?.message || error?.data?.error || error;
      message.error(
        typeof errorMessage === "string"
          ? errorMessage
          : t("assistantPages.directory.inviteEmailError")
      );
    } finally {
      setIsInviting(false);
    }
  };

  const handleDeleteAssistant = (assistantId) => {
    const normalizedAssistantId = normalizeId(assistantId);

    if (!normalizedAssistantId) {
      message.error(
        t("assistantPages.directory.deleteError", "Identifiant de l'assistante introuvable.")
      );
      return;
    }

    setDeleteTargetId(normalizedAssistantId);
  };

  const confirmDeleteAssistant = async () => {
    setDeletingAssistantId(deleteTargetId);
    try {
      await removeAssistant(deleteTargetId).unwrap();
      message.success(
        t("assistantPages.directory.deleteSuccess", "Assistante supprimée.")
      );
      setDeleteTargetId("");
    } catch (error) {
      const errorMessage =
        error?.message || error?.data?.message || error?.data?.error;
      message.error(
        errorMessage ||
          t("assistantPages.directory.deleteError", "Impossible de supprimer l'assistante.")
      );
    } finally {
      setDeletingAssistantId("");
    }
  };

  const canSubmit =
    formData.fullName.trim() &&
    formData.email.trim() &&
    isValidPhone(normalizePhone(formData.phone));

  const isSearchActive = query.trim().length > 0;

  const toStatusLabel = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "active" || s === "available") return t("assistantPages.directory.status.available");
    if (s === "inactive" || s === "offline") return t("assistantPages.directory.status.offline");
    if (s === "pending" || s === "busy") return t("assistantPages.directory.status.busy");
    if (s === "available") return t("assistantPages.directory.status.available");
    if (s === "busy") return t("assistantPages.directory.status.busy");
    if (s === "offline") return t("assistantPages.directory.status.offline");
    return status;
  };

  return (
    <AppLayoutDoctor>
      <section className="assistants-page">
        <div className="assistants-page__surface">
          <header className="assistants-page__header">
            <div>
              <h1>{t("assistantPages.directory.title")}</h1>
              <p>{t("assistantPages.directory.subtitle")}</p>
            </div>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalOpen(true)}
            >
              {t("assistantPages.directory.addAssistant")}
            </Button>
          </header>

          <div className="assistants-page__toolbar">
            <div className="assistants-search-wrapper">
              <div className="assistants-search-bar">
                <span className="assistants-search-icon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>

                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t("assistantPages.directory.searchPlaceholder")}
                  className="assistants-search-input"
                />

                {isSearchActive && (
                  <button
                    type="button"
                    className="assistants-search-clear"
                    onClick={() => setQuery("")}
                    aria-label={t("assistantPages.directory.clearSearch")}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>

              {isSearchActive && (
                <div className="assistants-search-results">
                  {t("assistantPages.directory.resultsCount", { count: filteredAssistants.length })}
                </div>
              )}
            </div>
          </div>

          <div className="assistants-grid">
            {listLoading && (
              <div className="assistants-empty">{t("common.loading", "Chargement...")}</div>
            )}

            {!listLoading && listError && (
              <div className="assistants-empty">{String(listError)}</div>
            )}

            {filteredAssistants.map((assistant) => {
              const assistantKey = normalizeId(
                assistant.id || assistant._id || assistant.assistantId
              );
              return (
                <article className="assistant-card" key={assistantKey}>
                  <div className="assistant-card__head">
                    <h3>{assistant.fullName}</h3>
                    <div className="assistant-card__actions">
                      <Tag
                        color={
                          STATUS_COLORS[String(assistant.status || "").toLowerCase()] ||
                          (String(assistant.status || "").toLowerCase() === "active"
                            ? "green"
                            : String(assistant.status || "").toLowerCase() === "pending"
                            ? "orange"
                            : "default")
                        }
                      >
                        {toStatusLabel(assistant.status)}
                      </Tag>
                      <Dropdown
                        trigger={["click"]}
                        menu={{
                          items: [
                            {
                              key: "delete",
                              danger: true,
                              icon: <DeleteOutlined />,
                              label: t("common.delete", "Supprimer"),
                              disabled: deletingAssistantId === assistantKey,
                              onClick: () => {
                                handleDeleteAssistant(
                                  assistant.id || assistant._id || assistant.assistantId
                                );
                              },
                            },
                          ],
                        }}
                      >
                        <Button
                          type="text"
                          icon={<MoreOutlined />}
                          aria-label={t("common.actions", "Actions")}
                        />
                      </Dropdown>
                    </div>
                  </div>

                  <p>
                    <strong>{t("common.email")}:</strong> {assistant.email}
                  </p>
                  <p>
                    <strong>{t("assistantPages.directory.phone")}:</strong> {assistant.phone}
                  </p>
                </article>
              );
            })}

            {!listLoading && !listError && filteredAssistants.length === 0 && (
              <div className="assistants-empty">{t("assistantPages.directory.empty")}</div>
            )}
          </div>
        </div>

        <Modal
          open={Boolean(deleteTargetId)}
          title={t("assistantPages.directory.deleteTitle", "Supprimer cette assistante ?")}
          onCancel={() => setDeleteTargetId("")}
          onOk={confirmDeleteAssistant}
          okText={t("common.delete", "Supprimer")}
          cancelText={t("common.cancel", "Annuler")}
          okButtonProps={{ danger: true }}
          confirmLoading={Boolean(deletingAssistantId)}
          centered
        >
          {t(
            "assistantPages.directory.deleteDescription",
            "Cette action supprimera définitivement son compte et son accès."
          )}
        </Modal>

        <Modal
          title={t("assistantPages.directory.modalTitle")}
          open={isModalOpen}
          className="assistants-modal"
          onCancel={() => setIsModalOpen(false)}
          onOk={handleAddAssistant}
          okText={t("assistantPages.directory.add")}
          cancelText={t("common.cancel")}
          width={560}
          centered
          okButtonProps={{ disabled: !canSubmit, loading: isInviting }}
          destroyOnClose
        >
          <div className="assistants-form">
            <label>
              {t("assistantPages.directory.fullName")}
              <Input
                value={formData.fullName}
                onChange={(event) => updateField("fullName", event.target.value)}
                placeholder={t("assistantPages.directory.fullNamePlaceholder")}
              />
            </label>

            <label>
              {t("common.email")}
              <Input
                type="email"
                value={formData.email}
                onChange={(event) => updateField("email", event.target.value)}
                placeholder={t("assistantPages.auth.emailPlaceholder")}
              />
            </label>

            <label>
              {t("assistantPages.directory.phone")}
              <Input
                value={formData.phone}
                onChange={(event) => updateField("phone", event.target.value)}
                placeholder={t("assistantPages.directory.phonePlaceholder")}
              />
            </label>

          </div>
        </Modal>
      </section>
    </AppLayoutDoctor>
  );
}